import React from 'react'
import { Grid } from '@mui/material'
import CarbonChart, { CHART_HEIGHT } from './CarbonChart'
import type { ChartDefinition } from '../social-media/chart-types'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'

export interface ThemeBreakdownProps {
  /** The charts to draw, in the order they should appear. */
  definitions: ChartDefinition[]
  /** Chart id → its table, as returned by useSkyCharts. */
  data: Record<string, PlatinumData | null>
  isLoading?: boolean
  /** Columns on a medium-and-up screen. 2 is the dashboards' default. */
  columns?: 1 | 2 | 3
  height?: number
  useGradients?: boolean
  hideLegend?: boolean
}

/**
 * A responsive grid of crosstab charts — the "how does this department's
 * conversation break down" block.
 *
 * WHY IT TAKES DEFINITIONS RATHER THAN CHILDREN. A department's opinion about
 * its dashboard is almost always *which* breakdowns it wants and in what order,
 * and that is a list. Expressing it as a list means a product page states it as
 * data — one array — rather than as a dozen lines of JSX that have to be
 * re-indented every time the order changes. Sports asking for emotion first and
 * sentiment second is then a one-line diff.
 *
 * `spacing={0}` with the charts' own borders is the edge-to-edge convention the
 * rest of the page uses; a grid gap would break the hairline alignment with the
 * KPI strip above.
 */
export const ThemeBreakdown: React.FC<ThemeBreakdownProps> = ({
  definitions,
  data,
  isLoading = false,
  columns = 2,
  height = CHART_HEIGHT,
  useGradients = true,
  hideLegend = false,
}) => {
  const md = columns === 1 ? 12 : columns === 3 ? 4 : 6

  return (
    <Grid container spacing={0}>
      {definitions.map((definition) => (
        <Grid item xs={12} md={md} key={definition.id}>
          <CarbonChart
            definition={definition}
            data={data[definition.id]}
            isLoading={isLoading}
            height={height}
            useGradients={useGradients}
            hideLegend={hideLegend}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default ThemeBreakdown
